import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAppointmentStatuses } from "../../hooks/useAppointmentStatuses";
import { useTouchScrollLock } from "../../hooks/useTouchScrollLock";
import { PatientSearch } from "../SearchPatients/SearchPatients";
import PatientWhatsAppConsent from "../WhatsApp/PatientWhatsAppConsent";
import {
  fetchUserServices,
  getUserPracticeById,
  getAppointmentsByPracticeIds,
  createAppointment,
  calculateAppointment,
  checkRecurringSchedule,
  createRecurringAppointments,
} from "../../api";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import { createIntakeSession, getIntakeEnabledForPractice } from "../../intakeApi";
import ServiceLinePicker from "../Services/ServiceLinePicker";
import AppointmentTimeWarnings, {
  warningMessages,
} from "./AppointmentTimeWarnings";
import { getUserPreferences } from "../BootNav/userPreferences";
import "./FullCalendar.css";

export function AddAppointmentModal({
  show,
  onHide,
  selectedDate,
  resourcesId,
  resourcesName,
  addNotification,
  setEvents,
  practiceIds,
}) {
  const [patient, setPatient] = useState({});
  const { toast, showToast, hideToast } = useToast();
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState(null);
  const [servicesDur, setServicesDur] = useState();
  const [selectedServicesDescr, setSelectedServicesDescr] = useState();
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState([]);
  const [selectedServicesId, setSelectedServicesId] = useState([]);
  const [newPatient, setNewPatient] = useState(false);
  const [sendIntakeEmail, setSendIntakeEmail] = useState(false);
  const [sendIntakeSMS, setSendIntakeSMS] = useState(false);
  const [intakeEnabled, setIntakeEnabled] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const modalBodyRef = useRef(null);
  // Keeps the calendar behind the modal from scrolling on iPad, which is what
  // desynchronises touch coordinates from the painted modal once the keyboard
  // opens. See the hook for the full explanation.
  useTouchScrollLock(show);
  // Availability warnings for the current Start/End window, rendered directly
  // under those two inputs. They used to be an info banner above the modal
  // body, which sat too far from the fields that caused them.
  //
  // The suggested-alternative-times state that lived here went with it: the
  // backend stopped returning `suggestions` when it stopped trimming
  // appointments to fit, so the branch that set it could never run and nothing
  // ever rendered it.
  const [timeWarnings, setTimeWarnings] = useState([]);
  const [errors, setErrors] = useState({});
  const [recurring, setRecurring] = useState(false);
  const [lastChangedField, setLastChangedField] = useState("numberOfSessions");
  // Only the dates that CAN'T be scheduled. The old flow needed a Check
  // Schedule click and then listed every date that could be booked: a wall of
  // text confirming the ordinary case. What a user needs to see is the
  // exception.
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [scheduleChecking, setScheduleChecking] = useState(false);
  // Guards against a slow early response overwriting a newer one.
  const scheduleReqRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [locationsForPractice, setLocationsForPractice] = useState([]);
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(0);

  function formatPhoneIntlZA(raw) {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");

    // already +27XXXXXXXXX
    if (/^\+27\d{9}$/.test(raw)) return raw;

    // 27XXXXXXXXX → +27XXXXXXXXX
    if (digits.startsWith("27") && digits.length === 11) return `+${digits}`;

    // 0XXXXXXXXX → +27XXXXXXXXX
    if (digits.startsWith("0") && digits.length === 10)
      return `+27${digits.slice(1)}`;

    // 9-digit local (missing leading 0) → +27XXXXXXXXX
    if (digits.length === 9) return `+27${digits}`;

    // fallback: if looks numeric, prefix +
    if (digits.length > 0 && !raw.startsWith("+")) return `+${digits}`;

    return raw.replace(/\s+/g, "");
  }

  function formatDateToInputValue(dateString) {
    const date = new Date(Date.parse(dateString)); // safer parsing
    if (isNaN(date)) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  const dateObj = new Date(selectedDate);

  // Build a locations array from a practice (backward compatible)
  function deriveLocationsFromPractice(practice) {
    if (!practice) return [];

    // If practice already has a non-empty locations array, use it
    if (Array.isArray(practice.locations) && practice.locations.length > 0) {
      return practice.locations;
    }

    // Otherwise synthesize from legacy address fields
    return [
      {
        name: practice.primaryLocationName || "Main Location",
        address1: practice.practiceAddress || "",
        address2: practice.practiceAddress2 || "",
        city: practice.practiceCity || "",
        province: practice.practiceProvince || "",
        postalCode: practice.practicePostalCode || "",
      },
    ];
  }

  const initialRecurringData = {
    recurringStartTime: dateObj.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    recurringEndTime: "",
    // Week step: 1 = every week, 2 = every second week.
    recurringInterval: 1,
    numberOfSessions: 1,
    recurringStartDate: dateObj.toISOString().split("T")[0],
    recurringEndDate: "",
    recurringDays: [],
  };

  const initialAppointment = {
    title: "",
    patientFirstName: "",
    patientLastName: "",
    start: selectedDate || "",
    end: "",
    patientId: "",
    serviceId: "",
    reminderInterval: [],
    reminderType: [],
    notes: "",
    resourceName: resourcesName || "",
    resourceId: resourcesId || "",
    practiceId: "",
    status: "Pending",
    eventColor: "",
    patientEmail: "",
    patientPhone: "",
    borderColor: "",
    backgroundColor: "",
    appointmentCreatedBy: "",
    appointmentCreatedDate: "",
    appointmentConfirmationEmail: !!getUserPreferences().autoConfirmAppointments,
    recurringData: "",
    appointmentLocation: {},
  };
  const [appointment, setAppointment] = useState(initialAppointment);

  // Statuses this modal writes are resolved by TYPE, so a practice that renamed
  // or replaced one still gets its own status rather than a stale literal.
  const {
    statusKeyForType,
    resolve: resolveStatus,
  } = useAppointmentStatuses(practiceIds);
  const [recurringData, setRecurringData] = useState(initialRecurringData);
  // Once the user hand-edits the End field, suppress the auto-recalc on
  // start/service change so end doesn't snap back to start + duration.
  // Cleared on modal close.
  const endManuallyEditedRef = useRef(false);
  // Intake session created for the current form, if any. Kept in a ref so a
  // retry after a failed save reuses it instead of emailing the patient a
  // second form. Cleared on close and after a successful save.
  const intakeTokenRef = useRef(null);

  useEffect(() => {
    if (!show || !selectedDate || !resourcesId) return;

    setAppointment((prevState) => ({
      ...prevState,
      start: formatDateToInputValue(selectedDate),
      resourceId: resourcesId,
      resourceName: resourcesName,
    }));

    setRecurringData((prevState) => ({
      ...prevState,
      recurringStartTime:
        dateObj.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }) || "",
      recurringStartDate: dateObj.toISOString().split("T")[0] || "",
    }));

    // Fetch services and practices in parallel
    const loadData = async () => {
      setServicesLoading(true);
      setServicesError(null);

      try {
        const [servicesResponse, practicesResponse] = await Promise.all([
          fetchUserServices(resourcesId),
          getUserPracticeById(resourcesId),
        ]);

        // Set services
        if (Array.isArray(servicesResponse) && servicesResponse.length > 0) {
          setServices(servicesResponse);
        } else {
          setServicesError("No services available for this practitioner");
          setServices([]);
        }

        // Set practices
        if (Array.isArray(practicesResponse) && practicesResponse.length > 0) {
          setPractices(practicesResponse);
          const firstPractice = practicesResponse[0];
          const locs = deriveLocationsFromPractice(firstPractice);

          setSelectedPracticeId(firstPractice.practiceNumber);
          setLocationsForPractice(locs);
          setSelectedLocationIdx(0);

          setAppointment((prev) => ({
            ...prev,
            practiceId: firstPractice.practiceNumber,
            practiceName: firstPractice.practiceName || "",
            location: { ...locs[0] },
            locationName: locs[0].name || "Location",
            locationAddress: [
              locs[0].address1,
              locs[0].address2,
              [locs[0].city, locs[0].province].filter(Boolean).join(", "),
              locs[0].postalCode,
            ]
              .filter(Boolean)
              .join(" | "),
          }));
        }
      } catch (error) {
        console.error("Error loading appointment data:", error);
        setServicesError("Failed to load services. Please try again.");
        setServices([]);
      } finally {
        setServicesLoading(false);
      }
    };

    loadData();
  }, [show, selectedDate, resourcesId, resourcesName]);

  useEffect(() => {
    setAppointment((prevState) => ({
      ...prevState,
      notes: selectedServicesDescr,
    }));
  }, [appointment.serviceId, selectedServicesDescr]);

  useEffect(() => {
    if (appointment.practiceId !== "" && !newPatient) {
      setAppointment((prevState) => ({
        ...prevState,
        status: statusKeyForType("Confirmed", appointment.practiceId),
      }));
    }
  }, [appointment.practiceId, newPatient]);

  // Check if intake is enabled for the selected practice (T&C configured)
  useEffect(() => {
    if (!appointment.practiceId) { setIntakeEnabled(false); return; }
    getIntakeEnabledForPractice(appointment.practiceId).then(setIntakeEnabled);
  }, [appointment.practiceId]);

  useEffect(() => {
    if (newPatient) {
      const updatedTitle = `${appointment.patientFirstName || ""} ${
        appointment.patientLastName || ""
      }`.trim();
      setAppointment((prevState) => ({
        ...prevState,
        title: updatedTitle,
      }));
    } else if (patient && Object.keys(patient).length > 0) {
      const updatedTitle = `${patient.patientTitle || ""} ${
        patient.patientFirstName || ""
      } ${patient.patientLastName || ""}`.trim();
      setAppointment((prevState) => ({
        ...prevState,
        title: updatedTitle,
        patientId: patient._id,
      }));
    }
  }, [
    patient,
    newPatient,
    appointment.patientFirstName,
    appointment.patientLastName,
  ]);

  // Filter practices to only those the selected patient is linked to
  const filteredPractices = useMemo(() => {
    const linked = patient?.patientPractice;
    if (!Array.isArray(linked) || linked.length === 0) return practices;
    const linkedSet = new Set(linked.map(String));
    return practices.filter((p) => linkedSet.has(String(p.practiceNumber)));
  }, [practices, patient]);

  // Only the practitioner's services that are set up for the chosen practice.
  // A service another practice offers must never be booked here: invoicing
  // that appointment then finds no such service for the practice. A service
  // with no practices recorded is not offered anywhere.
  const practiceServices = useMemo(() => {
    const practiceKey = String(appointment.practiceId || "");
    if (!practiceKey) return [];
    return services.filter(
      (s) =>
        Array.isArray(s.servicesPractice) &&
        s.servicesPractice.map(String).includes(practiceKey)
    );
  }, [services, appointment.practiceId]);

  // Switching practice drops a service the new practice does not offer.
  useEffect(() => {
    if (!appointment.serviceId || servicesLoading) return;
    if (practiceServices.some((s) => s._id === appointment.serviceId)) return;
    setAppointment((prev) => ({ ...prev, serviceId: "" }));
    setServicesDur(undefined);
    setSelectedServicesId(null);
    setSelectedServicesDescr("");
  }, [practiceServices, appointment.serviceId, servicesLoading]);

  // When the filtered practice list changes, reset to the first valid practice if current is no longer in the list
  useEffect(() => {
    if (!filteredPractices.length) return;
    const stillValid = filteredPractices.some(
      (p) => String(p.practiceNumber) === String(selectedPracticeId)
    );
    if (!stillValid) {
      const first = filteredPractices[0];
      const locs = deriveLocationsFromPractice(first);
      setSelectedPracticeId(first.practiceNumber);
      setLocationsForPractice(locs);
      setSelectedLocationIdx(0);
      setAppointment((prev) => ({
        ...prev,
        practiceId: first.practiceNumber,
        practiceName: first.practiceName || "",
        location: { ...locs[0] },
        locationName: locs[0]?.name || "Location",
        locationAddress: [
          locs[0]?.address1,
          locs[0]?.address2,
          [locs[0]?.city, locs[0]?.province].filter(Boolean).join(", "),
          locs[0]?.postalCode,
        ]
          .filter(Boolean)
          .join(" | "),
      }));
    }
  }, [filteredPractices]);

  useEffect(() => {
    if (!recurring || !recurringData.recurringStartDate) return;

    const {
      recurringDays,
      recurringStartDate,
      recurringEndDate,
      recurringInterval,
      numberOfSessions,
    } = recurringData;

    const dayMap = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    const recurringDayNums = recurringDays.map((day) => dayMap[day]);
    if (recurringDayNums.length === 0) return;

    const start = new Date(recurringStartDate);

    // Mirrors isActiveRecurringWeek on the server: with a week step of 2 only
    // every second week counts, so Sessions and End Date have to skip the same
    // weeks the backend will. Weeks start on Sunday, and week 0 is the one the
    // series starts in.
    const interval = Math.max(1, Math.floor(Number(recurringInterval) || 1));
    const startOfWeek = (d) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      x.setDate(x.getDate() - x.getDay());
      return x;
    };
    const anchorWeek = startOfWeek(start);
    const inActiveWeek = (date) =>
      interval === 1 ||
      Math.round(
        (startOfWeek(date) - anchorWeek) / (7 * 24 * 60 * 60 * 1000),
      ) % interval === 0;

    if (lastChangedField === "recurringEndDate" && recurringEndDate) {
      const end = new Date(recurringEndDate);
      let sessionCount = 0;
      for (
        let date = new Date(start);
        date <= end;
        date.setDate(date.getDate() + 1)
      ) {
        if (recurringDayNums.includes(date.getDay()) && inActiveWeek(date)) {
          sessionCount++;
        }
      }

      setRecurringData((prev) => ({
        ...prev,
        numberOfSessions: sessionCount,
      }));
    }

    if (lastChangedField === "numberOfSessions" && numberOfSessions > 0) {
      let sessionsRemaining = numberOfSessions;
      let currentDate = new Date(start);

      while (sessionsRemaining > 0) {
        if (recurringDayNums.includes(currentDate.getDay()) && inActiveWeek(currentDate)) {
          sessionsRemaining--;
        }
        if (sessionsRemaining > 0) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      setRecurringData((prev) => ({
        ...prev,
        recurringEndDate: currentDate.toISOString().split("T")[0],
      }));
    }
  }, [
    recurringData.numberOfSessions,
    recurringData.recurringEndDate,
    recurringData.recurringDays,
    recurringData.recurringStartDate,
    recurringData.recurringInterval,
    recurring,
    lastChangedField,
  ]);

  const handleChangeAppointment = async (e) => {
    const { type, name, value, checked } = e.target;

    // Always update the appointment state first
    setAppointment((prevState) => ({
      ...prevState,
      [name]: name === "newPatient" ? checked : value,
    }));

    // Handle "newPatient" logic
    if (name === "newPatient") {
      setNewPatient(checked);
      setAppointment((prevState) => ({
        ...prevState,
        patientId: checked ? "" : patient._id,
        status: statusKeyForType(
          checked ? "NewPatient" : "Confirmed",
          prevState.practiceId
        ),
      }));
      return;
    }

    // Handle "scheduling" toggle
    if (name === "scheduling") {
      setScheduling(checked);
      setAppointment((prevState) => ({
        ...prevState,
        appointmentConfirmationEmail: !checked,
        status: statusKeyForType(
          checked ? "Scheduling" : newPatient ? "NewPatient" : "Confirmed",
          prevState.practiceId
        ),
        borderColor: checked ? "#D3D3D3" : "",
        backgroundColor: checked ? "#D3D3D3" : "",
        title: checked ? "Unavailable" : "",
      }));

      return;
    }

    const RECUR_FIELDS = new Set([
      "recurringStartTime",
      "recurringEndTime",
      "numberOfSessions",
      "recurringStartDate",
      "recurringEndDate",
      "recurringDays",
    ]);
    if (RECUR_FIELDS.has(name)) {
      setRecurringData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "numberOfSessions" || name === "recurringEndDate") {
      setLastChangedField(name);
    }

    if (
      name === "recurringStartTime" &&
      recurring && // only in recurring mode
      servicesDur && // must have a duration from your selected service
      appointment.start // need the base date
    ) {
      const [durH, durM] = servicesDur.split(":").map(Number);
      const [startH, startM] = value.split(":").map(Number);

      // build a Date on the appointment.start day
      const base = new Date(appointment.start);
      base.setHours(startH, startM, 0, 0);

      // add the service duration in minutes
      const end = new Date(base.getTime() + (durH * 60 + durM) * 60000);

      const formattedEnd = end.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      // push it back into your recurringData
      setRecurringData((prev) => ({
        ...prev,
        recurringStartTime: value,
        recurringEndTime: formattedEnd,
      }));
    }

    // ✅ When "start" changes, re-check the new window and recalculate the end.
    //
    // The hand-edited-end guard used to sit on this whole block, so a user who
    // set End themselves and then moved Start got no availability check at all.
    // It now guards only the write to `end` (further down): the request always
    // goes out so the warnings stay true to the current Start, and an end the
    // user owns is still never snapped back to start + duration.
    if (name === "start" && appointment.serviceId) {
      const selectedService = services.find(
        (s) => s._id === appointment.serviceId
      );

      if (selectedService) {
        const appointmentData = {
          appointment: {
            start: value,
            resourceId: appointment.resourceId,
            practitionerName: resourcesName || "",
            // Lets the backend resolve conflicts against this practice's own
            // statuses instead of falling back to the seeded defaults.
            practiceId: appointment.practiceId,
          },
          selectedService: {
            servicesDuration: selectedService.servicesDuration,
            serviceId: selectedService._id,
          },
        };

        try {
          const result = await calculateAppointment(appointmentData);

          if (result?.endTime) {
            if (!endManuallyEditedRef.current) {
              setAppointment((prev) => ({
                ...prev,
                end: formatDateToInputValue(new Date(result.endTime)),
              }));
            }
            setTimeWarnings(warningMessages(result.warnings));
          }
        } catch (error) {
          console.error(
            "❌ Error recalculating appointment on start change:",
            error
          );
        }
      }
    }
  };

  const handleServiceChange = async (e) => {
    const selectedServiceId = e.target.value;
    const selectedService = services.find(
      (service) => service._id === selectedServiceId
    );

    if (!selectedService) return;

    setServicesDur(selectedService.servicesDuration);
    setSelectedServicesId(selectedService._id);
    setSelectedServicesDescr(selectedService.servicesDescription);

    // Commit the selection before the round-trip. The end-time call is a
    // convenience: if it fails, the user's chosen service must still stick,
    // otherwise the picker silently reads as empty.
    setAppointment((prevState) => ({
      ...prevState,
      serviceId: selectedServiceId,
      patientId: patient._id,
    }));

    const appointmentData = {
      appointment: {
        start: appointment.start || selectedDate,
        resourceId: appointment.resourceId,
        // Lets the backend resolve conflicts against this practice's own
        // statuses instead of falling back to the seeded defaults.
        practiceId: appointment.practiceId,
      },
      selectedService: {
        servicesDuration: selectedService.servicesDuration,
        serviceId: selectedService._id,
      },
    };

    try {
      if (!recurring) {
        const result = await calculateAppointment(appointmentData);

        if (!result) {
          // calculateAppointment swallows transport errors and returns null.
          // Say so rather than leaving End mysteriously blank.
          setTimeWarnings([
            "Could not calculate the end time for this service. Please set the End time manually.",
          ]);
          return;
        }

        if (result.endTime) {
          setAppointment((prevState) => ({
            ...prevState,
            // Only adopt the duration-derived end when the user hasn't already
            // overridden it. Keeps a hand-edited end intact when the service
            // is changed afterwards.
            end: endManuallyEditedRef.current
              ? prevState.end
              : formatDateToInputValue(new Date(result.endTime)),
          }));

          setTimeWarnings(warningMessages(result.warnings));
        }
      }
      // The recurring end time is NOT derived here: see the effect below.
    } catch (error) {
      console.error("Error calculating appointment end time:", error);
    }
  };

  /**
   * The recurring block's End Time, derived from the service duration.
   *
   * This used to live inside handleServiceChange, so it only ran when the
   * SERVICE changed while recurring was already on. Picking a service first and
   * switching to recurring afterwards (the natural order) left End Time
   * blank, and the only way to fill it was to clear the service and choose it
   * again. As an effect it fires on whichever of the three inputs lands last,
   * so every order works.
   */
  useEffect(() => {
    if (!recurring) return;
    const startTime = recurringData.recurringStartTime;
    if (!servicesDur || !startTime) return;

    const [durH, durM] = String(servicesDur).split(":").map(Number);
    const [startH, startM] = String(startTime).split(":").map(Number);
    if ([durH, durM, startH, startM].some((n) => Number.isNaN(n))) return;

    const base = new Date();
    base.setHours(startH, startM, 0, 0);
    const end = new Date(base.getTime() + (durH * 60 + durM) * 60000);
    const formatted = end.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    setRecurringData((prev) =>
      // Guarded: this effect re-runs whenever recurringData changes, and
      // setting an identical value would loop.
      prev.recurringEndTime === formatted
        ? prev
        : { ...prev, recurringEndTime: formatted },
    );
  }, [recurring, servicesDur, recurringData.recurringStartTime]);

  /**
   * Everything the availability check depends on, as one string.
   *
   * This is the debounce key AND the cache key: React compares deps by value,
   * so an edit that lands back on the same inputs (retyping a date, toggling a
   * day off and on) produces an identical signature and the effect below never
   * re-runs. Null while the form is incomplete, which is what stops the check
   * firing on every keystroke of a half-typed date.
   */
  const scheduleSignature = useMemo(() => {
    if (!recurring) return null;
    const {
      recurringDays,
      recurringStartTime,
      recurringStartDate,
      recurringEndDate,
      recurringInterval,
    } = recurringData;
    if (!appointment.resourceId || !servicesDur || !selectedServicesId) return null;
    if (
      !recurringDays?.length ||
      !recurringStartTime ||
      !recurringStartDate ||
      !recurringEndDate
    ) {
      return null;
    }
    return JSON.stringify({
      days: [...recurringDays].sort(),
      recurringStartTime,
      recurringStartDate,
      recurringEndDate,
      recurringInterval,
      servicesDur,
      selectedServicesId,
      resourceId: appointment.resourceId,
    });
  }, [
    recurring,
    recurringData,
    appointment.resourceId,
    servicesDur,
    selectedServicesId,
  ]);

  /**
   * Check availability automatically, replacing the Check Schedule button.
   *
   * Three things keep this off the database: it only runs once the form is
   * complete (signature is null otherwise), it waits for typing to settle, and
   * an unchanged signature is not a new dependency so the effect doesn't fire
   * at all. A settled edit costs exactly one request.
   */
  useEffect(() => {
    if (!scheduleSignature) {
      setScheduleConflicts([]);
      setScheduleChecking(false);
      return undefined;
    }
    const handle = setTimeout(async () => {
      const seq = ++scheduleReqRef.current;
      setScheduleChecking(true);
      try {
        const result = await checkRecurringSchedule({
          appointment: {
            start: recurringData.recurringStartDate,
            resourceId: appointment.resourceId,
          },
          selectedService: {
            servicesDuration: servicesDur,
            serviceId: selectedServicesId,
          },
          recurringData,
        });
        // A slower earlier request must not overwrite a newer answer.
        if (seq !== scheduleReqRef.current) return;
        setScheduleConflicts(Array.isArray(result?.conflicts) ? result.conflicts : []);
      } catch (error) {
        console.error("Error checking schedule:", error);
        // Clear rather than keep stale conflicts against inputs that have moved
        // on: a wrong warning is worse than none, and Save still re-checks
        // server-side before creating anything.
        if (seq === scheduleReqRef.current) setScheduleConflicts([]);
      } finally {
        if (seq === scheduleReqRef.current) setScheduleChecking(false);
      }
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSignature]);

  function validateForm() {
    let newErrors = {};

    if (newPatient) {
      if (
        !appointment.patientEmail ||
        !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(appointment.patientEmail)
      ) {
        newErrors.patientEmail = "Enter a valid email address.";
      }
      if (
        appointment.patientPhone &&
        !/^\+27\d{9}$/.test(appointment.patientPhone)
      ) {
        newErrors.patientPhone = "Enter a valid SA phone, e.g. +27821234567.";
      }
      if (!appointment.patientFirstName)
        newErrors.patientFirstName = "First name is required.";
      if (!appointment.patientLastName)
        newErrors.patientLastName = "Last name is required.";
    }

    if (!scheduling) {
      if (!appointment.serviceId)
        newErrors.serviceId = "Service selection is required.";
      else if (!practiceServices.some((s) => s._id === appointment.serviceId))
        newErrors.serviceId =
          "This service is not set up for the selected practice. Choose one of the practice's services.";
      if (!appointment.practiceId)
        newErrors.practiceId = "Practice selection is required.";
    }

    // Both start and end are required so we never persist `end: ""`, which
    // later renders as 1970-01-01T02:00 in the modal (epoch in SAST). The
    // recurring path manages its own date range and skips the single-end
    // picker, so it doesn't need this guard.
    if (!recurring) {
      if (!appointment.start) {
        newErrors.start = "Start time is required.";
      }
      if (!appointment.end) {
        newErrors.end = "End time is required.";
      }
    }

    if (appointment.start && appointment.end) {
      const s = new Date(appointment.start).getTime();
      const e = new Date(appointment.end).getTime();
      if (!isNaN(s) && !isNaN(e) && s >= e) {
        newErrors.end = "End time must be after the start time.";
      }
    }

    if (!newPatient && !scheduling && !appointment.patientId) {
      newErrors.patientId = "A patient must be selected.";
    }

    // Title is always required
    if (!appointment.title || !appointment.title.trim()) {
      newErrors.title = "Title is required.";
    }

    setErrors(newErrors);

    console.log("Validation Errors:", newErrors); // 🔹 Debugging step

    return Object.keys(newErrors).length === 0; // Return false if errors exist
  }

  async function handleSaveAppointment() {
    if (!validateForm()) {
      console.error("Validation failed!");
      return;
    }

    // Ensure a valid location is set (backward compatible)
    let finalAppt = { ...appointment };
    if (!finalAppt.location) {
      const p = practices.find(
        (x) => x.practiceNumber === finalAppt.practiceId
      );
      const locs = deriveLocationsFromPractice(p);
      const loc = locs[0] || {};
      finalAppt.location = { ...loc };
      finalAppt.locationName = loc.name || "Location";
      finalAppt.locationAddress = [
        loc.address1,
        loc.address2,
        [loc.city, loc.province].filter(Boolean).join(", "),
        loc.postalCode,
      ]
        .filter(Boolean)
        .join(" | ");
    }

    // The spinner covers the WHOLE save, the intake call included: that call
    // sends the patient an email, and an enabled Save button during it
    // invited double-clicks that sent duplicate intake forms.
    setScheduling(true);
    setIsLoading(true);

    // If either intake checkbox is checked, create the session now and attach
    // the tokenId. A session already created on an earlier failed attempt is
    // reused, so retrying a save never emails the patient a second form.
    if ((sendIntakeEmail && appointment.patientEmail) || (sendIntakeSMS && appointment.patientPhone)) {
      try {
        if (!intakeTokenRef.current) {
          const intakeResult = await createIntakeSession({
            practiceNumber: selectedPracticeId || appointment.practiceId,
            patientName: [appointment.patientFirstName, appointment.patientLastName].filter(Boolean).join(" "),
            deliveryEmail: sendIntakeEmail ? appointment.patientEmail : null,
            deliverySMS: sendIntakeSMS ? appointment.patientPhone : null,
          });
          if (intakeResult.tokenId) intakeTokenRef.current = intakeResult.tokenId;
        }
        if (intakeTokenRef.current) {
          finalAppt = { ...finalAppt, intakeSessionTokenId: intakeTokenRef.current };
        }
      } catch {
        // Non-fatal: appointment still saves without intake link
      }
    }

    // Only the save itself may report "NOT saved". Everything after it
    // (closing, resetting, the notification, the calendar refresh) runs
    // outside this try: a post-save exception used to land in this catch and
    // tell the user an appointment that HAD been saved was not, and
    // re-booking it created a duplicate.
    try {
      if (!recurring) {
        await createAppointment(finalAppt);
      } else {
        const data = {
          appointment: {
            ...finalAppt,
            start: recurringData.recurringStartDate,
            recurringData,
          },
          selectedService: {
            servicesDuration: servicesDur,
            serviceId: selectedServicesId,
          },
          recurringData: recurringData,
        };
        await createRecurringAppointments(data);
      }
    } catch (error) {
      // The save itself failed. Say so with the server's reason and keep the
      // form intact, so the user can retry without retyping (and without a
      // duplicate intake email, thanks to intakeTokenRef above).
      console.error("Error saving appointment:", error);
      const serverMessage =
        error?.response?.data?.error || error?.response?.data?.message;
      const httpStatus = error?.response?.status;
      const reason =
        serverMessage ||
        (httpStatus ? `HTTP ${httpStatus}` : error?.message || "Unknown error");
      showToast(
        "error",
        `The appointment was NOT saved (${reason}). Your details are still in the form. Please try again.`
      );
      setScheduling(false);
      setIsLoading(false);
      return;
    }

    try {
      // The appointment is saved. Close and confirm BEFORE the cosmetic
      // calendar refresh below: a refresh failure used to strand the modal
      // open with no message while the appointment (and the intake email)
      // had in fact gone through, and clicking Save again then created a
      // duplicate.
      showToast("success", "The appointment was successfully added!");
      onHide();
      intakeTokenRef.current = null;
      setSelectedServicesDescr("");
      setSelectedServicesId("");
      setSelectedPracticeId("");
      setNewPatient(false);
      setServicesDur("");
      setPatient({});
      setAppointment(initialAppointment);
      setRecurringData(initialRecurringData);
      setRecurring(false);
      setSendIntakeEmail(false);
      setSendIntakeSMS(false);
      // Don't clear services - they'll be refetched when modal opens

      // addNotification is optional: neither the calendar nor the profile
      // schedule passes it.
      if (
        typeof addNotification === "function" &&
        appointment.appointmentConfirmationEmail &&
        appointment.patientEmail
      ) {
        addNotification({
          message: `Confirmation email scheduled to ${appointment.patientEmail}`,
          timestamp: Date.now(),
          type: "email",
        });
      }

      // Calendar refresh, best effort. getAppointmentsByPracticeIds returns
      // null on failure rather than throwing, so guard the shape; a miss here
      // only means the new appointment shows after the next reload.
      try {
        const updatedAppointments = await getAppointmentsByPracticeIds(
          practiceIds
        );
        if (Array.isArray(updatedAppointments)) {
          setEvents(
            updatedAppointments.map((evt) => ({
              ...evt,
              extendedProps: {
                ...evt,
                patientEmail: evt.patientEmail,
                patientPhone: evt.patientPhone,
              },
            }))
          );
        } else {
          console.error("Calendar refresh after save returned no data.");
        }
      } catch (refreshError) {
        console.error("Calendar refresh after save failed:", refreshError);
      }
    } catch (postSaveError) {
      // The appointment IS saved; the success toast has already shown. A
      // failure in the tidy-up must never be reported as a failed save.
      console.error("Post-save step failed:", postSaveError);
    } finally {
      setScheduling(false);
      setIsLoading(false);
    }
  }

  function handleClose() {
    intakeTokenRef.current = null;
    setAppointment(initialAppointment);
    setRecurringData(initialRecurringData);
    setSelectedServicesDescr("");
    setSelectedPracticeId("");
    setNewPatient(false);
    setSelectedServicesId("");
    setServicesDur("");
    setPatient({});
    setTimeWarnings([]);
    setScheduling(false);
    setRecurring(false);
    endManuallyEditedRef.current = false;
    onHide();
  }

  const handleEndTimeChange = (e) => {
    setAppointment((prevState) => ({
      ...prevState,
      end: e.target.value,
    }));
    // Mark end as user-overridden so subsequent start/service changes
    // don't snap it back to start + duration.
    endManuallyEditedRef.current = true;
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size={recurring ? "xl" : "lg"}
        centered
        scrollable
        dialogClassName="responsive-modal appointment-modal"
      >
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0 d-flex align-items-center gap-2">
            <i className="fa-solid fa-calendar-plus" />
            Add Appointment
            {appointment.status && (
              <span
                className="fw-semibold small d-inline-flex align-items-center"
                style={{
                  color: resolveStatus(appointment.status, appointment.practiceId)
                    .color,
                }}
              >
                <i
                  className={`fa-solid ${
                    resolveStatus(appointment.status, appointment.practiceId).icon
                  } me-1`}
                />
                {resolveStatus(appointment.status, appointment.practiceId).label}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body ref={modalBodyRef}>
          <Form>
            {/* The three mode switches share one row: they were three stacked
                rows, which pushed the whole form down before it even began. */}
            <Row className="mb-2 g-2 align-items-center">
              <Form.Group as={Col} xs="auto" controlId="schedulingSwitch">
                <Form.Check
                  className="mb-0"
                  type="switch"
                  label="Staff Scheduling"
                  name="scheduling"
                  checked={scheduling}
                  onChange={handleChangeAppointment}
                />
              </Form.Group>
              {!scheduling && (
                <>
                  <Form.Group as={Col} xs="auto" controlId="recurringSwitch">
                    <Form.Check
                      className="mb-0"
                      type="switch"
                      label="Recurring"
                      name="recurring"
                      checked={recurring}
                      disabled={newPatient}
                      onChange={(e) => {
                        setRecurring(e.target.checked);
                      }}
                    />
                  </Form.Group>
                  <Form.Group as={Col} xs="auto" controlId="newPatientSwitch">
                    <Form.Check
                      className="mb-0"
                      type="switch"
                      label="New Patient"
                      name="newPatient"
                      checked={newPatient}
                      disabled={recurring}
                      onChange={handleChangeAppointment}
                    />
                  </Form.Group>
                </>
              )}
            </Row>
            {!scheduling && (
              <>
                {!newPatient && (
                  <Row className="mb-2">
                    {/* Wrapped in a Col, because as a bare flex child of `.row` the
                        search box sized to its content instead of the row. */}
                    <Col>
                    <PatientSearch
                      onPatientSelect={(patient) => {
                        setPatient(patient);
                        console.log("selected patient:", patient);
                        setAppointment((prevState) => ({
                          ...prevState,
                          patientId: patient._id,
                          patientFirstName: patient.patientFirstName,
                          patientLastName: patient.patientLastName,
                          patientPhone: formatPhoneIntlZA(
                            patient.patientCellNumber
                          ),
                          patientEmail: patient.patientEmail,
                        }));
                      }}
                    />
                    {errors.patientId && (
                      <span className="text-danger small">{errors.patientId}</span>
                    )}
                    {/* Consent STATUS, not a stored field on the appointment:
                        it belongs to the patient, and duplicating it here would
                        give two sources of truth that drift. Renders nothing
                        unless the practice has the WhatsApp feature. */}
                    <PatientWhatsAppConsent
                      patientId={appointment.patientId}
                      className="mt-2"
                    />
                    </Col>
                  </Row>
                )}
                {newPatient && (
                  <>
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group controlId="patientEmail">
                          <Form.Label>Patient Email</Form.Label>
                          <Form.Control
                            name="patientEmail"
                            value={appointment.patientEmail || ""}
                            onChange={handleChangeAppointment}
                            isInvalid={!!errors.patientEmail}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.patientEmail}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="patientPhone">
                          <Form.Label>Patient Phone (optional)</Form.Label>
                          <Form.Control
                            type="tel"
                            name="patientPhone"
                            placeholder="+27821234567"
                            value={appointment.patientPhone || ""}
                            onChange={(e) => {
                              // keep raw while typing (prevents 2727 loops)
                              const v = e.target.value;
                              setAppointment((prev) => ({
                                ...prev,
                                patientPhone: v,
                              }));
                            }}
                            onBlur={(e) => {
                              // normalize ONLY when user leaves the field
                              const normalized = formatPhoneIntlZA(
                                e.target.value
                              );
                              setAppointment((prev) => ({
                                ...prev,
                                patientPhone: normalized,
                              }));
                            }}
                            inputMode="tel"
                            isInvalid={!!errors.patientPhone}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.patientPhone}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
                {/* Side by side: stacked full-width they left a tall empty
                    band between the patient search and the rest of the form. */}
                <Row className="mb-2 g-2">
                  <Form.Group as={Col} md={6} controlId="patientFirstName">
                    <Form.Label>Patient First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="patientFirstName"
                      value={appointment.patientFirstName}
                      onChange={handleChangeAppointment}
                      isInvalid={!!errors.patientFirstName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.patientFirstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={6} controlId="patientLastName">
                    <Form.Label>Patient Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="patientLastName"
                      value={appointment.patientLastName}
                      onChange={handleChangeAppointment}
                      isInvalid={!!errors.patientLastName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.patientLastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
              </>
            )}

            <Row className="mb-2 g-2">
              <Form.Group as={Col} md={6} controlId="appointmentTitle">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  placeholder="For new patients enter their First Name and Last Name"
                  value={appointment.title}
                  onChange={handleChangeAppointment}
                  isInvalid={!!errors.title}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="appointmentResourceId">
                <Form.Label>Therapist</Form.Label>
                <Form.Control
                  readOnly
                  type="text"
                  name="resourceName"
                  className="appt-readonly"
                  value={appointment.resourceName}
                  onChange={handleChangeAppointment}
                />
              </Form.Group>
            </Row>
            {!scheduling && (
              <>
                <Row className="mb-2">
                  <Form.Group controlId="appointmentServiceId">
                    <Form.Label>Service</Form.Label>
                    {/* Replaces the legacy Form.Select dropdown with the
                        shared ServiceLinePicker search experience. We pass
                        hideCustomCodes so the toggle to custom codes
                        doesn't appear: appointments only ever deal with
                        services. The handler shape mirrors the original
                        onChange so downstream effects keep working. */}
                    <ServiceLinePicker
                      services={practiceServices}
                      hideCustomCodes
                      isInvalid={!!errors.serviceId}
                      disabled={servicesLoading || practiceServices.length === 0}
                      selectedServiceName={
                        services.find(
                          (s) => s._id === appointment.serviceId
                        )?.servicesName || ""
                      }
                      onClear={() => {
                        // handleServiceChange bails out when no service is
                        // resolved (`if (!selectedService) return;`), so we
                        // can't route a "clear" through it, so clear the
                        // appointment state directly instead. The next
                        // onSelect will run handleServiceChange normally
                        // and re-trigger the calculate-end-time flow.
                        setAppointment((prev) => ({
                          ...prev,
                          serviceId: "",
                        }));
                        setServicesDur(undefined);
                        setSelectedServicesId(null);
                        setSelectedServicesDescr("");
                      }}
                      onSelect={(svcId) =>
                        handleServiceChange({
                          target: { name: "serviceId", value: svcId },
                        })
                      }
                    />
                    {errors.serviceId && (
                      <div className="text-danger small mt-1">
                        {errors.serviceId}
                      </div>
                    )}
                    {servicesLoading && !errors.serviceId && (
                      <div className="text-muted small mt-1">
                        Loading services…
                      </div>
                    )}
                    {!servicesLoading &&
                      servicesError &&
                      !errors.serviceId && (
                        <div className="text-danger small mt-1">
                          {servicesError}
                        </div>
                      )}
                    {!servicesLoading &&
                      !servicesError &&
                      practiceServices.length === 0 &&
                      !errors.serviceId && (
                        <div className="text-muted small mt-1">
                          {services.length === 0
                            ? "No services available"
                            : "None of this practitioner's services are set up for this practice."}
                        </div>
                      )}
                  </Form.Group>
                </Row>
              </>
            )}
            {!recurring && (
              <>
              <Row className="mb-2 g-2">
                <Form.Group as={Col} md={6} controlId="appointmentStart">
                  <Form.Label>Start</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="start"
                    value={appointment.start}
                    onChange={handleChangeAppointment}
                    isInvalid={!!errors.start}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.start}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md={6} controlId="appointmentEnd">
                  <Form.Label>End</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="end"
                    value={
                      appointment.end
                        ? formatDateToInputValue(appointment.end)
                        : ""
                    }
                    onChange={handleEndTimeChange}
                    isInvalid={!!errors.end}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.end}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              {/* Sits under both time fields, spanning the row, because a
                  warning can be caused by either one. */}
              <AppointmentTimeWarnings messages={timeWarnings} />
              </>
            )}
            {recurring && (
              <div className="appt-recurring mb-2">
                {/* Day picker: seven toggle chips replace the 7-row checkbox
                    column that made this panel the tallest thing in the modal.
                    Active chip is brand-primary per the toggle-picker rule. */}
                <Form.Label className="mb-1">Recurring on</Form.Label>
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => {
                    const selected = recurringData.recurringDays?.includes(day);
                    return (
                      <Button
                        key={day}
                        size="sm"
                        variant={selected ? "primary" : "outline-primary"}
                        active={selected}
                        style={{ minWidth: 92 }}
                        onClick={() => {
                          const updatedDays = selected
                            ? recurringData.recurringDays?.filter(
                                (d) => d !== day
                              )
                            : [...recurringData.recurringDays, day];

                          setRecurringData((prevData) => ({
                            ...prevData,
                            recurringDays: updatedDays,
                          }));
                        }}
                      >
                        {day}
                      </Button>
                    );
                  })}
                </div>

                {/* Six fields, md={2} each: exactly the 12-column grid, so
                    the row never wraps. Adding the Repeats picker had taken the
                    total to 14 and pushed End Date onto a line of its own. The
                    time and Sessions inputs are narrow because "09:00" and a
                    session count need far less room than a date. */}
                <Row className="g-2 align-items-end">
                  <Form.Group as={Col} xs={6} md={2} controlId="recurringStartTime">
                    <Form.Label>Start Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="recurringStartTime"
                      value={recurringData.recurringStartTime}
                      onChange={handleChangeAppointment}
                    />
                  </Form.Group>
                  <Form.Group as={Col} xs={6} md={2} controlId="recurringEndTime">
                    <Form.Label>End Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="recurringEndTime"
                      value={recurringData.recurringEndTime}
                      onChange={handleChangeAppointment}
                    />
                  </Form.Group>
                  <Form.Group as={Col} xs={6} md={2} controlId="recurringInterval">
                    <Form.Label>Repeats</Form.Label>
                    <Form.Select
                      name="recurringInterval"
                      value={recurringData.recurringInterval ?? 1}
                      onChange={(e) =>
                        setRecurringData((prev) => ({
                          ...prev,
                          recurringInterval: Number(e.target.value) || 1,
                        }))
                      }
                    >
                      <option value={1}>Every week</option>
                      <option value={2}>Every 2nd week</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group as={Col} xs={6} md={2} controlId="numberOfSessions">
                    <Form.Label>Sessions</Form.Label>
                    <Form.Control
                      type="number"
                      name="numberOfSessions"
                      value={recurringData.numberOfSessions}
                      onChange={handleChangeAppointment}
                      min={1}
                    />
                  </Form.Group>
                  <Form.Group as={Col} xs={6} md={2} controlId="recurringStartDate">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="recurringStartDate"
                      value={recurringData.recurringStartDate}
                      onChange={handleChangeAppointment}
                    />
                  </Form.Group>
                  <Form.Group as={Col} xs={6} md={2} controlId="recurringEndDate">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="recurringEndDate"
                      value={recurringData.recurringEndDate}
                      onChange={handleChangeAppointment}
                    />
                  </Form.Group>
                </Row>

                {/* Only what can't be booked. Availability is checked
                    automatically once the times and dates are set, so there is
                    no button and no list confirming the dates that are fine:
                    those are the expected case and reading them was work. */}
                {scheduleChecking && (
                  <div className="small text-muted mt-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Checking availability…
                  </div>
                )}
                {!scheduleChecking && scheduleConflicts.length > 0 && (
                  <Alert variant="warning" className="mt-2 mb-0 py-2">
                    <p className="mb-1 small fw-bold">
                      {scheduleConflicts.length}{" "}
                      {scheduleConflicts.length === 1 ? "date clashes" : "dates clash"}{" "}
                      with an existing appointment and won't be created:
                    </p>
                    <ul className="mb-0 ps-3 small">
                      {scheduleConflicts.map((conflict, idx) => (
                        <li key={`${conflict.date}-${idx}`}>
                          {conflict.date}: {conflict.startTime} - {conflict.endTime}
                        </li>
                      ))}
                    </ul>
                    <p className="mb-0 mt-1 small">
                      Every other date in the series will still be booked.
                    </p>
                  </Alert>
                )}
              </div>
            )}
            <Row className="mb-2">
              <Form.Group as={Col} controlId="appointmentConfirmationEmail">
                <Form.Check
                  type="checkbox"
                  label="Send confirmation email"
                  name="appointmentConfirmationEmail"
                  checked={appointment.appointmentConfirmationEmail}
                  onChange={(e) => {
                    const { name, checked } = e.target;
                    handleChangeAppointment({
                      target: { name, value: checked },
                    });
                  }}
                />
                {intakeEnabled && newPatient && (
                  <>
                    <Form.Check
                      type="checkbox"
                      label="Send Intake form via email"
                      className="mt-1"
                      checked={sendIntakeEmail}
                      disabled={!appointment.patientEmail}
                      title={!appointment.patientEmail ? "Patient email required" : ""}
                      onChange={(e) => setSendIntakeEmail(e.target.checked)}
                    />
                  </>
                )}
              </Form.Group>
            </Row>

            <Row className="mb-2">
              {/* "Booking note", not "Notes": this is the practice's memo on
                  the booking, shown read-only on the patient file. Clinical
                  notes live on the patient file's Notes section. */}
              <Form.Group controlId="appointmentNotes">
                <Form.Label>Booking note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={appointment.notes}
                  onChange={handleChangeAppointment}
                />
              </Form.Group>
            </Row>
            {!scheduling && (
            <Row className="mb-2 g-2">
              {/* Practice (existing) */}
              <Form.Group as={Col} md={6} controlId="appointmentPractice">
                <Form.Label>Practice</Form.Label>
                <Form.Select
                  name="practice"
                  value={selectedPracticeId || ""}
                  onChange={(e) => {
                    const practiceId = e.target.value;
                    const selectedPractice = (filteredPractices || []).find(
                      (p) => String(p.practiceNumber) === String(practiceId)
                    );

                    setSelectedPracticeId(practiceId);

                    // Derive locations (backward compatible with legacy address fields)
                    const locs =
                      Array.isArray(selectedPractice?.locations) &&
                      selectedPractice.locations.length > 0
                        ? selectedPractice.locations
                        : [
                            {
                              name:
                                selectedPractice?.primaryLocationName ||
                                "Main Location",
                              address1: selectedPractice?.practiceAddress || "",
                              address2:
                                selectedPractice?.practiceAddress2 || "",
                              city: selectedPractice?.practiceCity || "",
                              province:
                                selectedPractice?.practiceProvince || "",
                              postalCode:
                                selectedPractice?.practicePostalCode || "",
                            },
                          ];

                    setLocationsForPractice(locs);

                    // Always default to the first (primary) location
                    const nextIdx = 0;
                    setSelectedLocationIdx(nextIdx);
                    const firstLoc = locs[nextIdx] || {};

                    // Save practice + chosen location onto the appointment
                    setAppointment((prev) => ({
                      ...prev,
                      practiceId: practiceId,
                      practiceName: selectedPractice?.practiceName || "",
                      location: { ...firstLoc },
                      locationName: firstLoc.name || "Location",
                      locationAddress: [
                        firstLoc.address1,
                        firstLoc.address2,
                        [firstLoc.city, firstLoc.province]
                          .filter(Boolean)
                          .join(", "),
                        firstLoc.postalCode,
                      ]
                        .filter(Boolean)
                        .join(" | "),
                    }));
                  }}
                  isInvalid={!!errors.practiceId}
                >
                  <option value="" disabled>
                    Select a Practice
                  </option>
                  {Array.isArray(filteredPractices) && filteredPractices.length > 0 ? (
                    filteredPractices.map((p) => (
                      <option key={p.practiceNumber} value={p.practiceNumber}>
                        {p.practiceName}
                      </option>
                    ))
                  ) : (
                    <option disabled>No Practices Available</option>
                  )}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.practiceId}
                </Form.Control.Feedback>
              </Form.Group>

              {/* NEW: Location (sits to the right of Practice) */}
              <Form.Group as={Col} md={6} controlId="appointmentLocation">
                <Form.Label>Location</Form.Label>
                <Form.Select
                  name="locationIdx"
                  value={String(selectedLocationIdx ?? 0)}
                  onChange={(e) => {
                    const idx = Number(e.target.value);
                    const loc = (locationsForPractice || [])[idx] || {};
                    setSelectedLocationIdx(idx);

                    // Save the chosen location onto the appointment
                    setAppointment((prev) => ({
                      ...prev,
                      location: { ...loc },
                      locationName: loc.name || "Location",
                      locationAddress: [
                        loc.address1,
                        loc.address2,
                        [loc.city, loc.province].filter(Boolean).join(", "),
                        loc.postalCode,
                      ]
                        .filter(Boolean)
                        .join(" | "),
                    }));
                  }}
                  disabled={
                    !locationsForPractice || locationsForPractice.length === 0
                  }
                >
                  {Array.isArray(locationsForPractice) &&
                  locationsForPractice.length > 0 ? (
                    locationsForPractice.map((loc, idx) => (
                      <option key={`loc-${idx}`} value={String(idx)}>
                        {(loc.name || "Location") +
                          ": " +
                          [loc.address1, loc.city, loc.province]
                            .filter(Boolean)
                            .join(", ")}
                      </option>
                    ))
                  ) : (
                    <option value="0">No Location</option>
                  )}
                </Form.Select>
                <div className="form-text">
                  Defaults to the first location (primary).
                </div>
              </Form.Group>
            </Row>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSaveAppointment}
            style={{ minWidth: 140 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <i className="fa-regular fa-floppy-disk me-2" />
                Save
              </>
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            style={{ minWidth: 140 }}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />

    </>
  );
}
